import { useState, useEffect, useRef } from 'react';
import { TeamState, Timeout } from '@/types';

interface TimeoutModalProps {
  isOpen: boolean;
  teamA: TeamState;
  teamB: TeamState;
  setNumber: number;
  activeTimeoutId: string | null;
  activeTimeoutTeamId: string | null;
  activeTimeoutStartTime: string | null;
  onStartTimeout: (teamId: string) => Promise<void>;
  onEndTimeout: () => Promise<void>;
  onClose: () => void;
  completedTimeouts?: Timeout[];
}

export default function TimeoutModal({
  isOpen,
  teamA,
  teamB,
  setNumber,
  activeTimeoutId,
  activeTimeoutTeamId,
  activeTimeoutStartTime,
  onStartTimeout,
  onEndTimeout,
  onClose,
  completedTimeouts = [],
}: TimeoutModalProps) {
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isStarting, setIsStarting] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // localActive bridges the gap between onStartTimeout success and parent prop update
  const [localActive, setLocalActive] = useState(false);
  const [localTeamId, setLocalTeamId] = useState<string | null>(null);
  const [localStartTime, setLocalStartTime] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isActive = !!activeTimeoutId && !!activeTimeoutStartTime;
  const showActive = isActive || localActive;

  const effectiveTeamId = activeTimeoutTeamId ?? localTeamId;
  const effectiveStartTime = activeTimeoutStartTime ?? localStartTime;
  const activeTeam = effectiveTeamId === teamA.id ? teamA : teamB;

  const activeTeamTimeoutCount = effectiveTeamId
    ? completedTimeouts.filter(t => t.teamId === effectiveTeamId).length + 1
    : 1;

  // When real props arrive, clear local bridge state
  useEffect(() => {
    if (isActive && localActive) {
      setLocalActive(false);
      setLocalTeamId(null);
      setLocalStartTime(null);
    }
  }, [isActive, localActive]);

  // Update elapsed time when in active timeout
  useEffect(() => {
    if (showActive && effectiveStartTime) {
      const updateElapsed = () => {
        const start = new Date(effectiveStartTime).getTime();
        const now = Date.now();
        const elapsed = Math.floor((now - start) / 1000);
        setElapsedSeconds(elapsed);
      };

      updateElapsed();
      timerRef.current = setInterval(updateElapsed, 1000);

      return () => {
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      };
    } else if (!showActive) {
      setElapsedSeconds(0);
    }
  }, [showActive, effectiveStartTime]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const handleStartTimeout = async () => {
    if (!selectedTeamId) return;
    setIsStarting(true);
    setError(null);
    try {
      const startedAt = new Date().toISOString();
      await onStartTimeout(selectedTeamId);
      // Stay on modal — switch to in-progress view immediately
      setLocalTeamId(selectedTeamId);
      setLocalStartTime(startedAt);
      setLocalActive(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start timeout');
    } finally {
      setIsStarting(false);
    }
  };

  const handleEndTimeout = async () => {
    setIsEnding(true);
    setError(null);
    try {
      await onEndTimeout();
      setSelectedTeamId(null);
      setLocalActive(false);
      setLocalTeamId(null);
      setLocalStartTime(null);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to end timeout');
    } finally {
      setIsEnding(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-surface border border-border rounded-2xl p-6 w-full max-w-sm shadow-2xl">

        {!showActive ? (
          /* ── TEAM SELECTION ── */
          <>
            <h2 className="text-xl font-bold text-text-primary text-center mb-5">
              Select Team Taking Timeout
            </h2>

            <div className="space-y-3 mb-5">
              <button
                onClick={() => setSelectedTeamId(teamA.id)}
                className={`w-full py-4 px-5 rounded-xl font-bold text-base transition-all flex items-center gap-3 border ${
                  selectedTeamId === teamA.id
                    ? 'border-2'
                    : 'border-border bg-surface-raised hover:border-border/80'
                }`}
                style={
                  selectedTeamId === teamA.id
                    ? { borderColor: teamA.color }
                    : {}
                }
              >
                <span
                  className="w-4 h-4 rounded-full flex-shrink-0"
                  style={{ backgroundColor: teamA.color }}
                />
                <span className="text-text-primary">{teamA.name}</span>
              </button>

              <button
                onClick={() => setSelectedTeamId(teamB.id)}
                className={`w-full py-4 px-5 rounded-xl font-bold text-base transition-all flex items-center gap-3 border ${
                  selectedTeamId === teamB.id
                    ? 'border-2'
                    : 'border-border bg-surface-raised hover:border-border/80'
                }`}
                style={
                  selectedTeamId === teamB.id
                    ? { borderColor: teamB.color }
                    : {}
                }
              >
                <span
                  className="w-4 h-4 rounded-full flex-shrink-0"
                  style={{ backgroundColor: teamB.color }}
                />
                <span className="text-text-primary">{teamB.name}</span>
              </button>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleStartTimeout}
                disabled={!selectedTeamId || isStarting}
                className="w-full py-4 px-4 rounded-xl font-bold text-base bg-warning text-black transition-all hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {isStarting ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    STARTING...
                  </span>
                ) : (
                  'START TIMEOUT'
                )}
              </button>

              <button
                onClick={onClose}
                className="w-full py-3 px-4 rounded-xl font-semibold text-sm bg-surface-raised text-text-secondary border border-border transition-all hover:bg-surface"
              >
                CANCEL
              </button>
            </div>

            {error && (
              <div className="mt-4 bg-error/20 border border-error rounded-lg p-3">
                <p className="text-error text-sm text-center">{error}</p>
              </div>
            )}
          </>
        ) : (
          /* ── TIMEOUT IN PROGRESS ── */
          <>
            <div className="flex items-center justify-center gap-2 mb-2">
              <span
                className="w-3 h-3 rounded-full animate-pulse flex-shrink-0"
                style={{ backgroundColor: activeTeam?.color }}
              />
              <span className="font-bold text-base uppercase tracking-wide" style={{ color: activeTeam?.color }}>
                TIMEOUT {activeTeamTimeoutCount} IN PROGRESS
              </span>
            </div>

            <p className="text-text-muted text-sm text-center mb-4">
              Set {setNumber}
            </p>

            <div
              className="rounded-xl p-4 mb-4 border"
              style={{ borderColor: activeTeam?.color }}
            >
              <p className="text-text-muted text-xs text-center mb-1">Team</p>
              <p className="font-bold text-lg text-center" style={{ color: activeTeam?.color }}>
                {activeTeam?.name}
              </p>
            </div>

            <p className="text-text-muted text-sm text-center mb-2">Elapsed Time</p>
            <div className="font-mono-nums text-6xl font-bold text-text-primary text-center mb-5">
              {formatTime(elapsedSeconds)}
            </div>

            <button
              onClick={handleEndTimeout}
              disabled={isEnding}
              className="w-full py-4 px-4 rounded-xl font-bold text-base bg-success text-white transition-all hover:opacity-90 disabled:opacity-60"
            >
              {isEnding ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ENDING...
                </span>
              ) : (
                'END TIMEOUT'
              )}
            </button>

            {error && (
              <div className="mt-4 bg-error/20 border border-error rounded-lg p-3">
                <p className="text-error text-sm text-center">{error}</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Timeout Completed Display Component
export function TimeoutCompletedDisplay({
  team,
  durationSeconds,
}: {
  team: TeamState;
  durationSeconds: number;
}) {
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  return (
    <div className="bg-success/10 border border-success/30 rounded-lg p-4 text-center">
      <div className="text-sm text-success font-bold uppercase mb-2">Timeout Completed</div>
      <div className="flex items-center justify-center gap-2 mb-2">
        <span className="text-xs text-text-muted">Team:</span>
        <span className="font-bold text-sm" style={{ color: team.color }}>
          {team.shortName}
        </span>
      </div>
      <div className="flex items-center justify-center gap-2">
        <span className="text-xs text-text-muted">Duration:</span>
        <span className="font-mono-nums font-bold text-text-primary">
          {formatDuration(durationSeconds)}
        </span>
      </div>
    </div>
  );
}
